import { makeQueryOptions } from '@mint-lib/api-zod-support/react-query';

import { camelToSnake } from '@mint-lib/api';
import { defaultApi, defaultClient } from '@mint-sdk/api-core';
import { z } from 'zod';

import {
  CreateDataFileRequestSchema,
  CreateDataFileResponseSchema,
  DataFileFilterOptionsQuerySchema,
  DataFileFilterOptionsResponseSchema,
  DataFileFormatsQueryResult,
  DatafileFormatsResponseSchema,
  DataFilePreviewRowsResponseSchema,
  DataFilePreviewV1ResponseSchema,
  DataFilePreviewV2ResponseSchema,
  DataFileSheetNamesSchema,
  DataFilesV1QueryResult,
  DataFilesV1ResponseSchema,
  HeadersFormatsOptionsQueryResult,
  HeadersFormatsOptionsResponseSchema,
  HeadersMappingQueryResult,
  HeadersMappingSchema,
  PreviewRowsQueryResult,
  SetDataFileSheetNamePayloadSchema,
  SetFooterRowsCountPayloadSchema,
  TableDataFileFooterRowsQueryResult,
  TableDataFileFooterRowsResponseSchema,
  TableDataFilePreviewV1QueryResult,
  TableDataFilePreviewV2QueryResult,
  TableDataFilesV1QuerySchema,
  TableDataFilesV2QueryResult,
  TableDataFilesV2QuerySchema,
  TableDataFilesV2ResponseSchema,
  UpdateContextRequestSchema,
} from './schemas.js';

const transformSortToOrdering = <
  T extends { sortBy?: string; sortDirection?: string },
>(
  data: T,
) => {
  const { sortBy, sortDirection, ...rest } = data;

  // Convert sortBy and sortDirection to the sorting format expected by the API
  if (sortBy && sortDirection) {
    const snakeFieldName = camelToSnake(sortBy);
    const ordering =
      sortDirection === 'desc' ? `-${snakeFieldName}` : snakeFieldName;

    return {
      ...rest,
      ordering,
    };
  }

  return data;
};

const TableDataFilesV1QuerySchemaWithTransform =
  TableDataFilesV1QuerySchema.transform(transformSortToOrdering);

const TableDataFilesV2QuerySchemaWithTransform =
  TableDataFilesV2QuerySchema.transform(transformSortToOrdering);

export const listDataFilesV1QueryOptions = defaultClient.query({
  url: 'data-ingestion/tabledatafile/' as const,
  method: 'GET',
  querySchema:
    TableDataFilesV1QuerySchemaWithTransform as unknown as typeof TableDataFilesV1QuerySchema,
  responseSchema: DataFilesV1ResponseSchema,
  snakeCaseQueryParams: true,
  processResponse(data): DataFilesV1QueryResult {
    return data.data;
  },
  keyPrefix: ['dataFilesList'],
});

export const listDataFilesV2QueryOptions = defaultClient.query({
  url: 'data-ingestion/tabledatafile/v2/' as const,
  method: 'GET',
  querySchema:
    TableDataFilesV2QuerySchemaWithTransform as unknown as typeof TableDataFilesV2QuerySchema,
  responseSchema: TableDataFilesV2ResponseSchema,
  snakeCaseQueryParams: true,
  processResponse(data): TableDataFilesV2QueryResult {
    return data.data;
  },
  keyPrefix: ['dataFilesList'],
});

export const createDataFile = defaultApi.declareEndpoint({
  url: 'data-ingestion/tabledatafile/' as const,
  method: 'POST',
  requestSchema: CreateDataFileRequestSchema,
  responseSchema: CreateDataFileResponseSchema,
});

export const createDataFileV2 = defaultApi.declareEndpoint({
  url: 'data-ingestion/tabledatafile/v2/' as const,
  method: 'POST',
  requestSchema: CreateDataFileRequestSchema,
  responseSchema: CreateDataFileResponseSchema,
});

export const getTableDataFilePreviewV1QueryOptions = defaultClient.query({
  url: 'data-ingestion/tabledatafile/$id/preview/' as const,
  method: 'GET',
  responseSchema: DataFilePreviewV1ResponseSchema,
  processResponse(data): TableDataFilePreviewV1QueryResult {
    const preview = data.data.items[0];
    if (preview.error === 'domain_error') {
      preview.error = null;
    }
    return preview;
  },
});

export const getTableDataFilePreviewV2QueryOptions = defaultClient.query({
  url: 'data-ingestion/tabledatafile/$id/preview/v2/' as const,
  method: 'GET',
  responseSchema: DataFilePreviewV2ResponseSchema,
  processResponse(data): TableDataFilePreviewV2QueryResult {
    const preview = data.data.items[0];
    if (preview.error === 'domain_error') {
      preview.error = null;
    }
    return preview;
  },
});

export const updateTableDataFileContext = defaultApi.declareEndpoint({
  url: 'data-ingestion/tabledatafile/$id/context/' as const,
  method: 'POST',
  requestSchema: UpdateContextRequestSchema,
  responseSchema: z.any(),
});

export const updateTableDataFileContextV2 = defaultApi.declareEndpoint({
  url: 'data-ingestion/tabledatafile/$id/context/v2/' as const,
  method: 'POST',
  requestSchema: UpdateContextRequestSchema,
  responseSchema: z.any(),
});

export const listDataFileSheetNames = defaultApi.declareEndpoint({
  url: `data-ingestion/tabledatafile/$dataFileId/sheet-names/` as const,
  method: 'GET',
  responseSchema: DataFileSheetNamesSchema,
});

export const listDataFileSheetNamesQueryOptions = makeQueryOptions(
  listDataFileSheetNames,
  {
    processResponse(data) {
      return data.data.items[0].sheets;
    },
  },
);

export const setDataFileSheetName = defaultApi.declareEndpoint({
  url: `data-ingestion/tabledatafile/$dataFileId/set-sheet-name/` as const,
  method: 'POST',
  requestSchema: SetDataFileSheetNamePayloadSchema,
  responseSchema: z.any(),
});

export const getDataFilePreviewRowsQueryOptions = defaultClient.query({
  url: 'data-ingestion/tabledatafile/$id/preview-rows/' as const,
  method: 'GET',
  responseSchema: DataFilePreviewRowsResponseSchema,
  processResponse(data): PreviewRowsQueryResult {
    return data.data;
  },
});

export const getTableDataFileFooterRows = defaultApi.declareEndpoint({
  url: 'data-ingestion/tabledatafile/$dataFileId/footer-rows/' as const,
  method: 'GET',
  responseSchema: TableDataFileFooterRowsResponseSchema,
});

export const getTableDataFileFooterRowsQueryOptions = makeQueryOptions(
  getTableDataFileFooterRows,
  {
    processResponse(data): TableDataFileFooterRowsQueryResult {
      return data.data;
    },
  },
);

export const setFooterRowsCount = defaultApi.declareEndpoint({
  url: 'data-ingestion/tabledatafile/$dataFileId/set-footer-rows-count/' as const,
  method: 'POST',
  requestSchema: SetFooterRowsCountPayloadSchema,
  responseSchema: z.object({}),
});

export const getHeadersMappingQueryOptions = defaultClient.query({
  url: 'data-ingestion/tabledatafile/$id/mapped-headers/' as const,
  method: 'GET',
  responseSchema: HeadersMappingSchema,
  processResponse(data): HeadersMappingQueryResult {
    return data.data;
  },
  keyPrefix: [
    'headers_mapping' satisfies TableDataFilePreviewV1QueryResult['currentStep'],
  ],
});

export const getDataFileFormats = defaultApi.declareEndpoint({
  url: 'data-ingestion/tabledatafile/$id/formats/' as const,
  method: 'GET',
  responseSchema: DatafileFormatsResponseSchema,
});

export const getDataFileFormatsQueryOptions = makeQueryOptions(
  getDataFileFormats,
  {
    processResponse(data): DataFileFormatsQueryResult {
      return data.data.items[0];
    },
  },
);

export const getDataFileFilterOptionsV1QueryOptions = defaultClient.query({
  url: 'data-ingestion/tabledatafile/filters/' as const,
  method: 'GET',
  querySchema: DataFileFilterOptionsQuerySchema,
  responseSchema: DataFileFilterOptionsResponseSchema,
  snakeCaseQueryParams: true,
  processResponse(data) {
    return data.data.items[0];
  },
});

export const getDataFileFilterOptionsV2QueryOptions = defaultClient.query({
  url: 'data-ingestion/tabledatafile/filters/v2/' as const,
  method: 'GET',
  querySchema: DataFileFilterOptionsQuerySchema,
  responseSchema: DataFileFilterOptionsResponseSchema,
  snakeCaseQueryParams: true,
  processResponse(data) {
    return data.data.items[0];
  },
});

export const getHeadersFormatsOptionsQueryOptions = defaultClient.query({
  url: 'data-ingestion/tabledatafile/formats/meta/' as const,
  method: 'GET',
  responseSchema: HeadersFormatsOptionsResponseSchema,
  processResponse(data): HeadersFormatsOptionsQueryResult {
    return data.data.items[0];
  },
});
